import { z } from 'zod';

/**
 * Pricing rule schemas — discriminated union by `kind`.
 *
 * Stored in DB as a row in `pricing_rules` with `kind` and `params` (JSON).
 * The `params` JSON for each kind is validated by the matching schema below.
 *
 * Composition:
 *   - tolerance and minimum can each appear at most once (1 active rule).
 *   - fraction, hour and daily can be combined; the pricing engine picks the cheapest
 *     applicable per minute window and caps the total per daily window.
 *   - When multiple rules of the same kind are active, the one with higher `priority`
 *     wins (handled outside this schema, by the pricing engine).
 */

const cents = z.number().int().nonnegative().describe('amount in BRL cents');

/**
 * A price that is actually charged, so it cannot be zero (#411).
 *
 * A rule worth R$ 0,00 is never what the owner meant — it is a field they
 * opened and did not finish. Worse, it is invisible: a `daily` left at zero
 * caps every window to zero and the whole ticket closes free, with the
 * fraction and hour prices still sitting on screen looking correct.
 *
 * Deliberately NOT applied to `stepsCents`, where zero is a real offer: "1ª
 * hora grátis" is on boards all over the country.
 */
const priceCents = z.number().int().positive().describe('amount in BRL cents, never zero');

const positiveInt = z.number().int().positive();

export const toleranceParamsSchema = z.object({
  /** Até um dia: 99999 passava sem aviso (#518). */
  minutes: positiveInt.max(1440).describe('free minutes from arrival'),
  /**
   * Como a tolerância vale, escolha do estacionamento (#507). Desconto (sem
   * `mode`): a tolerância sai de toda estadia. Carência: quem sai dentro dela
   * não paga, e quem passa paga desde a entrada.
   */
  mode: z.enum(['grace', 'discount']).optional(),
});

/**
 * Faixas do começo da estadia (#487): "até 30 min R$ 5,00, até 1 h R$ 8,00".
 * O preço de cada faixa é o total até ali, como a placa escreve. Depois da
 * última faixa, o resto da regra cobra o que sobra como sempre.
 *
 * É o formato das leis que mudam a fração dentro da 1ª hora — em Maringá, a
 * Lei 9.964/2015 manda 30 min na 1ª hora e 15 min nas seguintes.
 *
 * Zero vale de propósito, igual ao `stepsCents`: "até 15 min grátis" é oferta
 * de verdade. Os preços não podem cair de uma faixa para a seguinte, senão
 * ficar mais tempo sairia mais barato.
 */
export const initialBandsSchema = z
  .array(
    z.object({
      upToMinutes: positiveInt.max(180).describe('the band covers stays up to these minutes'),
      amountCents: cents.describe('total price of a stay inside the band'),
    }),
  )
  .min(1)
  .max(6)
  .refine(
    (bands) => bands.every((b, i) => i === 0 || b.upToMinutes > (bands[i - 1]?.upToMinutes ?? 0)),
    'as faixas precisam crescer em minutos',
  )
  .refine(
    (bands) => bands.every((b, i) => i === 0 || b.amountCents >= (bands[i - 1]?.amountCents ?? 0)),
    'o preço de uma faixa não pode ser menor que o da anterior',
  );

export const fractionParamsSchema = z.object({
  blockMinutes: positiveInt.describe('size of each billing block in minutes'),
  amountCents: priceCents.describe('cost per block'),
  initialBands: initialBandsSchema.optional(),
});

export type InitialBand = z.infer<typeof initialBandsSchema>[number];

export const hourParamsSchema = z.object({
  amountCents: priceCents.describe('cost per full hour past the progressive steps'),
  /**
   * Progressive tariff (#396): price of the 1st, 2nd, ... hour, in order.
   * Every hour past the list costs `amountCents` — the "demais horas" or
   * "hora excedente" line on the boards lots hang at the gate.
   *
   * Omitted or empty keeps the flat tariff: every hour costs `amountCents`.
   *
   * The table restarts on every daily window, so a stay crossing midnight pays
   * the first hour again — the "para cada dia utilizado" the boards spell out.
   */
  stepsCents: z.array(cents).max(12).optional().describe('price of each first hour, in order'),
  /**
   * Hand the hours past `stepsCents` back to the fraction rule instead of
   * charging `amountCents` for each one (#408). This is the "hora excedente ou
   * fração" line: the board prices the first hour (or the first few) and bills
   * everything after it in blocks.
   *
   * Needs both a step list and a fraction rule to mean anything — with neither,
   * the flat `amountCents` still prices every hour.
   */
  remainderByFraction: z
    .boolean()
    .optional()
    .describe('hours past the steps are billed by the fraction rule'),
});

export const dailyParamsSchema = z.object({
  amountCents: priceCents.describe('cap per window'),
  windowHours: positiveInt.default(24).describe('window size in hours'),
  /**
   * Where the lot's day starts (#589). `midnight`, or absent, is the calendar
   * day: the cap and the table restart at midnight, the "para cada dia
   * utilizado" of the boards. `entry` counts `windowHours` from the car's
   * entry, so a stay crossing midnight keeps running on the same table. The
   * owner chooses; a per-day price (`day_rate`) always uses the calendar day.
   */
  dayStartsAt: z
    .enum(['midnight', 'entry'])
    .optional()
    .describe('the daily window starts at midnight or at the entry'),
});

/** Na lista de dias, o 7 é "Feriado" (#396). 0 a 6 seguem `Date#getDay`. */
export const HOLIDAY_WEEKDAY = 7;

/**
 * Dias em que uma regra vale, no relógio do lote (#396): 0 é domingo, 6 é
 * sábado e 7 é feriado. Em dia de feriado vale o 7, não o dia da semana: é o
 * "Sáb, Dom e Feriado" das placas, e um pernoite "2ª a 6ª" não cobra a noite
 * de uma segunda que é feriado.
 *
 * Pelo menos um dia, sem repetir — uma lista vazia seria uma regra ligada que
 * nunca vale, o mesmo engano do #411.
 */
export const weekdaysSchema = z
  .array(z.number().int().min(0).max(HOLIDAY_WEEKDAY))
  .min(1)
  .max(8)
  .refine((days) => new Set(days).size === days.length, 'dia da semana repetido')
  .describe('local weekdays, 0 = Sunday … 6 = Saturday, 7 = holiday');

/**
 * Pernoite (#396): preço da janela da noite, em hora local do lote.
 *
 * `endHour` menor ou igual a `startHour` significa que a janela atravessa a
 * meia-noite — "20h às 8h" é `startHour: 20, endHour: 8`.
 *
 * Entra como TETO dos minutos passados dentro da janela: quem dorme no pátio
 * paga o pernoite em vez das horas, e quem fica só um pedaço da noite nunca
 * paga mais do que ele. Assim a conta nunca passa da tabela exposta na placa.
 *
 * Precisa do fuso do lote para saber onde a noite começa; sem fuso a regra é
 * ignorada, do mesmo jeito que a diária cai para janela rolante.
 */
export const overnightParamsSchema = z.object({
  amountCents: priceCents.describe('price of one night window'),
  startHour: z.number().int().min(0).max(23).describe('local hour the night opens, e.g. 20'),
  endHour: z.number().int().min(0).max(23).describe('local hour the night closes, e.g. 8'),
  /**
   * Noites que a regra cobre, pelo dia em que a noite ABRE (#396). "Pernoite
   * (20:00 às 06:00, 2ª a 6ª)" é `[1, 2, 3, 4, 5]`: a noite de sexta abre na
   * sexta e entra; a de sábado fica para o preço do fim de semana. Ausente vale
   * todas as noites, que é o pernoite de antes.
   */
  weekdays: weekdaysSchema.optional(),
});

/**
 * Preço máximo de uma faixa do dia (#396).
 *
 * É a segunda diária das placas grandes: "Diária R$ 136,00" convivendo com
 * "Preço máximo (06:00 às 20:00) R$ 112,00". A diária é o teto do dia inteiro;
 * este é o teto de quem só usou o pátio dentro da faixa, e precisa ser menor —
 * senão a diária já limitaria tudo e o número digitado aqui não faria nada.
 *
 * Limita o custo dos minutos DENTRO da faixa. O que cair fora dela continua
 * cobrado normalmente, e a diária ainda se aplica por cima do conjunto.
 *
 * Igual ao pernoite, a faixa é hora local do lote: sem fuso a regra é ignorada.
 */
export const windowCapParamsSchema = z.object({
  amountCents: priceCents.describe('cap for the minutes inside the window'),
  startHour: z.number().int().min(0).max(23).describe('local hour the window opens, e.g. 6'),
  endHour: z.number().int().min(0).max(23).describe('local hour the window closes, e.g. 20'),
});

/**
 * Preço fechado por dia, em dias da semana escolhidos (#396).
 *
 * É o "Sáb, Dom e Feriado R$ 12,00 para cada dia utilizado" das placas grandes:
 * naqueles dias a tabela de horas não vale, e cada dia do relógio do pátio em
 * que o carro ficou custa o valor fechado. O pernoite continua cobrado à parte
 * nas noites que ele cobrir, e a diária ainda limita o dia por cima.
 *
 * Feriado fica de fora por enquanto: precisa de uma fonte de datas (nacionais,
 * móveis e municipais) que o sistema ainda não tem.
 *
 * Igual ao pernoite, o dia é do relógio do lote: sem fuso a regra é ignorada.
 */
export const dayRateParamsSchema = z.object({
  amountCents: priceCents.describe('price of each day used on the listed weekdays'),
  weekdays: weekdaysSchema,
});

export const minimumParamsSchema = z.object({
  amountCents: priceCents.describe('minimum charge after tolerance'),
});

/**
 * Fixed service fee charged per VALET ticket (#270), on top of the time-based
 * price. Applies to monthly subscribers too — the fee pays the manobrista
 * service, not the parking spot (courtesy validation still zeroes everything
 * at close).
 */
export const valetFeeParamsSchema = z.object({
  amountCents: priceCents.describe('fixed service fee per valet ticket'),
});

export type ToleranceParams = z.infer<typeof toleranceParamsSchema>;
export type FractionParams = z.infer<typeof fractionParamsSchema>;
export type HourParams = z.infer<typeof hourParamsSchema>;
export type DailyParams = z.infer<typeof dailyParamsSchema>;
export type OvernightParams = z.infer<typeof overnightParamsSchema>;
export type WindowCapParams = z.infer<typeof windowCapParamsSchema>;
export type DayRateParams = z.infer<typeof dayRateParamsSchema>;
export type MinimumParams = z.infer<typeof minimumParamsSchema>;
export type ValetFeeParams = z.infer<typeof valetFeeParamsSchema>;

export const pricingRuleKinds = [
  'tolerance',
  'fraction',
  'hour',
  'daily',
  'overnight',
  'window_cap',
  'day_rate',
  'minimum',
  'valet_fee',
] as const;
export type PricingRuleKind = (typeof pricingRuleKinds)[number];

/**
 * Discriminated union for a fully-typed rule (kind + matching params).
 * Used by API and pricing engine.
 */
export const pricingRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tolerance'), params: toleranceParamsSchema }),
  z.object({ kind: z.literal('fraction'), params: fractionParamsSchema }),
  z.object({ kind: z.literal('hour'), params: hourParamsSchema }),
  z.object({ kind: z.literal('daily'), params: dailyParamsSchema }),
  z.object({ kind: z.literal('overnight'), params: overnightParamsSchema }),
  z.object({ kind: z.literal('window_cap'), params: windowCapParamsSchema }),
  z.object({ kind: z.literal('day_rate'), params: dayRateParamsSchema }),
  z.object({ kind: z.literal('minimum'), params: minimumParamsSchema }),
  z.object({ kind: z.literal('valet_fee'), params: valetFeeParamsSchema }),
]);

export type PricingRule = z.infer<typeof pricingRuleSchema>;

/**
 * Validates a raw `params` payload for the given kind.
 * Use when reading a row from DB (where `kind` is a separate column from `params` JSON).
 */
export const parsePricingParams = (kind: PricingRuleKind, params: unknown): PricingRule => {
  const wrapped = pricingRuleSchema.parse({ kind, params });
  return wrapped;
};

/**
 * Alguma regra marca "Feriado" na lista de dias? Só então vale buscar o
 * calendário do lote para precificar (#396).
 */
export const rulesUseHolidays = (rules: readonly PricingRule[]): boolean =>
  rules.some(
    (r) =>
      (r.kind === 'overnight' || r.kind === 'day_rate') &&
      (r.params.weekdays?.includes(HOLIDAY_WEEKDAY) ?? false),
  );
